// Gorilla business.
//
// The idle breathing in render/gorilla.js keeps them alive; this is the other
// half of the job. Every few seconds one of them stops behaving and does
// something stupid: beats its chest, scratches its head, shrugs, picks its nose,
// taunts the other side of the skyline.
//
// An antic is a pure function of its own progress. It returns a pose the drawing
// reads, and it never touches the gorilla's position — so a gorilla mid-chest-beat
// is exactly as easy to hit as one standing still.
//
// Falling asleep lives here too, but it is not an antic: it is what happens to the
// gorilla left standing there while the other side dithers, and it has to be
// interruptible by a banana leaving the far fist, so it runs as its own little
// state machine with a startle on the way out.

import { SLEEP_AFTER_MS, STARTLE_MS } from './config.js';
import { state } from './state.js';
import { isAttract } from './state.js';
import { mods } from './modes.js';
import { unlockedAntics } from './progress.js';

/** Wait before the first bit of business after a new city (ms). */
const FIRST_GAP_MS = 1400;
/** Wait between antics (ms). */
const MIN_GAP_MS = 3000;
const MAX_GAP_MS = 8000;
/** Extra offset per gorilla, so the two never start goofing off in unison. */
const STAGGER_MS = 1500;

const TAU = Math.PI * 2;
/** Fast oscillation, -1..1, `hz` cycles per second, `t` in ms. */
const wobble = (t, hz) => Math.sin((t / 1000) * hz * TAU);

/** The free arm hangs on the opposite side from the throwing arm. */
const side = idx => (idx === 0 ? -1 : 1);

// --- the antics ------------------------------------------------------------
//
// Poses are offsets in px from the gorilla's anchor. For reference, the neutral
// pose puts the free fist at (side*26, 16) and the throwing fist at
// (-side*30, -6), the chest sits around y=0 and the head around y=-25.

const ANTICS = {
  /** Scratching its belly, with all the time in the world. */
  scratch: {
    ms: 2000,
    pose: (s, t) => ({
      freeFist: { x: s * 14, y: 4 + wobble(t, 4.5) * 4 },
      head: { x: 0, y: 1 },
      mouth: 0.2,
      eyes: 'open',
    }),
  },

  /** Chest beating, the way a gorilla settles an argument. */
  chest: {
    ms: 1500,
    pose: (s, t) => {
      const beat = wobble(t, 4);
      return {
        freeFist: { x: s * 11, y: beat > 0 ? 10 : 3 },
        throwFist: { x: -s * 11, y: beat > 0 ? 3 : 10 },
        head: { x: 0, y: -1 },
        mouth: 0.35,
        eyes: 'wide',
      };
    },
  },

  /** Puzzled head scratch. The hand stops just clear of the face. */
  head: {
    ms: 2100,
    pose: (s, t) => ({
      freeFist: { x: s * 13, y: -34 + wobble(t, 5) * 3 },
      head: { x: s * 2, y: 0 },
      mouth: 0.15,
      eyes: 'open',
    }),
  },

  /** Taunting whatever is on the other side of the skyline. */
  wave: {
    ms: 1900,
    pose: (s, t) => ({
      throwFist: { x: -s * 30 + wobble(t, 3) * 5, y: -48 },
      head: { x: 0, y: -1 },
      mouth: 0.5,
      eyes: 'wide',
    }),
  },

  /** Picking its nose. Crude, but there is no more gorilla thing than this. */
  pick: {
    ms: 1800,
    pose: (s, t) => ({
      freeFist: { x: s * 6, y: -17 + wobble(t, 3) * 1.5 },
      head: { x: 0, y: 1 },
      eyes: 'wide',
    }),
  },

  /** A shrug: no idea, do not ask me. */
  shrug: {
    ms: 1300,
    pose: s => ({
      freeFist: { x: s * 33, y: -4 },
      throwFist: { x: -s * 28, y: -2 },
      head: { x: 0, y: 2 },
      eyes: 'shut',
    }),
  },

  /** A big stretch and a yawn. */
  stretch: {
    ms: 2300,
    pose: (s, t) => {
      const up = Math.min(1, t / 700);
      const yawn = Math.sin((t / 2300) * Math.PI);
      return {
        freeFist: { x: s * (26 - up * 6), y: 16 - up * 62 },
        throwFist: { x: -s * (30 - up * 8), y: -6 - up * 58 },
        head: { x: 0, y: -up * 2 },
        mouth: yawn,
        eyes: yawn > 0.5 ? 'shut' : 'open',
      };
    },
  },

  /** Shaking itself off like a wet dog. */
  shake: {
    ms: 900,
    pose: (s, t) => ({
      jitter: wobble(t, 13) * 3,
      freeFist: { x: s * 30, y: 8 },
      throwFist: { x: -s * 32, y: -10 },
      mouth: 0.4,
      eyes: 'wide',
    }),
  },

  /** Clapping. The two fists meet in front of the chest and come apart again. */
  clap: {
    ms: 1400,
    pose: (s, t) => {
      // 0 to 1 and back: the hands are 36px apart at the far end of the swing and 20 at the near
      // one, which is as close as two fists of radius 5 can be and still read as two.
      const near = (wobble(t, 5) + 1) / 2;
      const out = 18 - near * 8;
      return {
        freeFist: { x: s * out, y: 2 },
        throwFist: { x: -s * out, y: 2 },
        head: { x: 0, y: -1 },
        mouth: 0.45,
        eyes: 'wide',
      };
    },
  },

  /** Both fists up and a look at whoever is watching. The chest does the rest. */
  flex: {
    ms: 1800,
    pose: (s, t) => {
      const up = Math.min(1, t / 400);
      return {
        freeFist: { x: s * (26 + up * 8), y: 16 - up * 44 },
        throwFist: { x: -s * (30 + up * 8), y: -6 - up * 40 },
        head: { x: 0, y: 1 + wobble(t, 3) },
        mouth: 0.3,
        eyes: 'wide',
      };
    },
  },
};

const NAMES = Object.keys(ANTICS);

/**
 * Picks a bit of business, never the same one twice in a row. Only the ones the
 * player has unlocked are eligible, which is what makes an achievement feel like
 * it changed the gorillas rather than just a list.
 */
function pickAntic(previous) {
  const allowed = unlockedAntics();
  const pool = NAMES.filter(name => allowed.has(name));
  const fresh = pool.filter(name => name !== previous);
  const from = fresh.length ? fresh : pool;

  return from.length ? from[Math.floor(Math.random() * from.length)] : null;
}

function gap() {
  // En el escaparate, la mitad: dos gorilas esperando turno en una portada es lo único que hay que
  // mirar, y una monería cada minuto no es un escaparate.
  const k = isAttract() ? 2 : 1;
  return (MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS)) / k;
}

/** True when this gorilla is lining up a shot, and must therefore hold still. */
function isAiming(idx) {
  if (state.projectile || state.matchOver) return false;
  if (state.turn !== idx) return false;
  // The CPU aims while you are watching the sliders, so let its gorilla fidget.
  return !(state.cpuOn && idx === 1);
}

/** True while this gorilla is stood about waiting for the other side to shoot. */
function isWaiting(idx) {
  return !state.matchOver && !state.projectile && state.turn !== idx;
}

// --- sleeping, and the jump on the way out --------------------------------

/**
 * Called when a shot goes off. Anyone asleep gets a fright.
 */
export function wakeGorillas() {
  state.players.forEach(p => {
    if (!p.asleep) return;
    p.asleep = false;
    p.startle = state.clock;
    p.antic = null;
  });
}

/** Slumped, eyes shut, jaw slack, three little z's drifting off. */
function sleepPose(s, t) {
  return {
    freeFist: { x: s * 20, y: 20 },
    throwFist: { x: -s * 22, y: 8 },
    // A slow, deep nod rather than the usual breathing. Kept to +y so the head
    // never lifts above where a shot would land anyway.
    head: { x: 0, y: 4 + wobble(t, 0.5) * 2 },
    mouth: 0.4,
    eyes: 'shut',
    zzz: true,
  };
}

/** Shot heard: off the ground, arms out, eyes like saucers. */
function startlePose(s, t) {
  const p = Math.min(1, t / STARTLE_MS);
  // Up fast, down slower.
  const lift = -Math.sin(Math.pow(p, 0.7) * Math.PI) * 15;

  return {
    lift,
    jitter: wobble(t, 16) * 2.2 * (1 - p),
    freeFist: { x: s * 34, y: -18 + lift * 0.6 },
    throwFist: { x: -s * 34, y: -20 + lift * 0.6 },
    head: { x: 0, y: -3 },
    mouth: 0.85,
    eyes: 'wide',
  };
}

/**
 * Runs the "what is it doing now" clock for both gorillas. Called once per frame
 * by the render loop, before drawing.
 */
export function advanceAntics() {
  // CLASSIC has no antics at all. Sleeping goes with them: it is the same
  // behaviour, and a gorilla nodding off in the middle of the original would
  // have been a bug report, not a feature.
  //
  // Y el escaparate no es CLASSIC: en la portada los dos gorilas son el anuncio, así que se mueven
  // aunque el modo guardado no tenga monerías, y con la mitad de espera entre una y la siguiente.
  const attract = isAttract();
  if (!mods().antics && !attract) return;

  // En el escaparate no hay nadie apuntando ni esperando turno: nadie ha pulsado nada, y lo único
  // que hay que hacer es no quedarse quieto. Medido sin esto: cero monerías en veinte segundos en la
  // portada, porque el dueño del turno estaba inmóvil "apuntando" y el otro se dormía a los pocos
  // segundos de espera. Los dos gags eran correctos y entre los dos no dejaban hacer nada.
  const aiming = idx => (attract ? false : isAiming(idx));
  const waiting = idx => (attract ? false : isWaiting(idx));

  const now = state.clock;

  // A zero means it has never been armed, so the first frame sets it rather than
  // treating the whole session so far as one enormous wait.
  if (!state.turnStartedAt) state.turnStartedAt = now;

  state.players.forEach((p, idx) => {
    if (!p.alive) return;

    // Mid-jump: nothing else gets a look in until it lands.
    if (p.startle) {
      if (now - p.startle >= STARTLE_MS) p.startle = 0;
      return;
    }

    if (p.asleep) {
      // Anything other than still waiting on the other side wakes it up.
      if (!waiting(idx) || now - state.turnStartedAt <= SLEEP_AFTER_MS) p.asleep = false;
      return;
    }

    // Nodding off. Only the gorilla stood waiting, and only once the wait has
    // dragged on long enough to be worth commenting on.
    if (waiting(idx) && now - state.turnStartedAt > SLEEP_AFTER_MS) {
      p.asleep = true;
      p.asleepAt = now;
      p.antic = null;
      p.nextAntic = now + gap();
      return;
    }

    if (!p.nextAntic) {
      p.nextAntic = now + FIRST_GAP_MS + idx * STAGGER_MS + Math.random() * 2000;
      return;
    }

    // The moment it becomes this gorilla's turn the show stops: the aiming hand
    // has to sit exactly where the banana will leave it, and the trajectory
    // preview is drawn from that point.
    if (aiming(idx)) {
      if (p.antic) {
        p.antic = null;
        p.nextAntic = now + gap();
      }
      return;
    }

    if (p.antic) {
      if (now - p.anticStarted >= ANTICS[p.antic].ms) {
        p.lastAntic = p.antic;
        p.antic = null;
        p.nextAntic = now + gap();
      }
      return;
    }

    if (now >= p.nextAntic) {
      const chosen = pickAntic(p.lastAntic);
      if (chosen) {
        p.antic = chosen;
        p.anticStarted = now;
      } else {
        p.nextAntic = now + gap();
      }
    }
  });
}

/**
 * The pose the current antic wants, or an empty object when the gorilla is just
 * standing there breathing.
 */
export function anticPose(idx, p) {
  // A fright overrides whatever the gorilla was in the middle of.
  if (p.startle) {
    const t = state.clock - p.startle;
    if (t < STARTLE_MS) return startlePose(side(idx), t);
  }

  if (p.asleep) return sleepPose(side(idx), state.clock - p.asleepAt);

  const spec = p.antic && ANTICS[p.antic];
  if (!spec) return {};
  return spec.pose(side(idx), state.clock - p.anticStarted) || {};
}
